import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { buildManualContext, filterManualModules } from '../../manual/manualContext';
import { allManualModules } from '../../manual';
import ManualFunctionSection from './ManualFunctionSection';
import './manual.css';

function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function ManualViewer() {
  const moduleSettings = useModuleSettings();
  const [activeId, setActiveId] = useState('');

  const ctx = useMemo(() => buildManualContext(moduleSettings), [moduleSettings]);
  const modules = useMemo(
    () => filterManualModules(allManualModules, ctx),
    [ctx, moduleSettings.loaded],
  );

  const functionTitleById = useMemo(() => {
    const map = {};
    allManualModules.forEach((mod) => {
      (mod.sections || []).forEach((sec) => {
        if (sec?.id) {
          map[sec.id] = sec.title || sec.name || sec.id;
        }
      });
    });
    return map;
  }, []);

  useEffect(() => {
    const onHash = () => {
      setActiveId(window.location.hash.replace(/^#/, ''));
      scrollToHash(window.location.hash);
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!moduleSettings.loaded) return;
    if (modules.length && !window.location.hash) {
      window.location.hash = modules[0].sections[0]?.id || modules[0].id;
    }
  }, [moduleSettings.loaded, modules]);

  if (!moduleSettings.loaded) {
    return <p className="text-gray-500 p-8">Cargando manual…</p>;
  }

  if (modules.length === 0) {
    return (
      <div className="p-8 max-w-3xl">
        <p className="text-gray-600">No hay módulos de manual visibles para su perfil y configuración.</p>
        <Link to="/dashboard" className="text-teal-700 font-medium mt-4 inline-block">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="manual-layout">
      <aside className="manual-toc" aria-label="Índice del manual">
        <div className="manual-toc-inner">
          <p className="manual-toc-title">Índice</p>
          <nav>
            {modules.map((mod) => (
              <div key={mod.id} className="manual-toc-module">
                <a href={`#${mod.id}`} className="manual-toc-mod-link">{mod.title}</a>
                <ul>
                  {mod.sections.map((sec) => (
                    <li key={sec.id}>
                      <a
                        href={`#${sec.id}`}
                        className={activeId === sec.id ? 'manual-toc-active' : ''}
                      >
                        {sec.kind === 'report' ? '📊 ' : ''}
                        {sec.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      <article className="manual-article">
        <header className="manual-header">
          <h1>Manual de usuario — Contifine Logistic</h1>
          <p className="manual-lead">
            Manual profesional con plantilla de 12 apartados por función (ubicación, interfaz, procedimiento,
            ejemplo, validaciones, errores frecuentes y más). Solo se listan módulos habilitados y permitidos
            para su usuario. Las capturas de pantalla se publicarán en una revisión posterior del manual.
          </p>
        </header>

        {modules.map((mod) => (
          <section key={mod.id} id={mod.id} className="manual-module">
            <h2 className="manual-h2">{mod.title}</h2>
            {mod.intro && <p className="manual-intro">{mod.intro}</p>}

            {mod.sections.map((sec) => (
              <div key={sec.id} id={sec.id} className="manual-section scroll-mt-4">
                <ManualFunctionSection section={sec} functionTitleById={functionTitleById} />
              </div>
            ))}
          </section>
        ))}

        <footer className="manual-footer">
          <p>
            Última revisión del manual: documentación en código ({' '}
            <code className="text-xs">frontend/src/manual/</code>). Ante cambios de pantallas, actualice
            el manual siguiendo la skill del proyecto <code className="text-xs">contify-manual-usuario</code>.
          </p>
        </footer>
      </article>
    </div>
  );
}
